<script setup lang="ts">
/**
 * Fullscreen image lightbox for the product detail page.
 *
 * Opens on cover-image click. Supports keyboard navigation (Esc / ←→),
 * backdrop dismissal, thumbnail strip, prev/next arrows. Image is requested
 * at 1600×1600 via Cloudflare Image Resizing (object-contain, no upscale).
 *
 * Two-way bound: parent passes images + initialIndex; the component emits
 * `update:index` so closing keeps the parent's selected image in sync.
 */

interface Props {
  modelValue: boolean
  images: string[]
  initialIndex?: number
  alt?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
  alt: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'update:index': [value: number]
}>()

const { t } = useI18n()
const currentIndex = ref(props.initialIndex)

// On open, jump to the parent's currently-selected image.
watch(() => props.modelValue, (open) => {
  if (open) currentIndex.value = props.initialIndex
})

watch(currentIndex, (i) => emit('update:index', i))

const total = computed(() => props.images.length)
const hasMultiple = computed(() => total.value > 1)

const close = () => emit('update:modelValue', false)
const next = () => {
  if (!hasMultiple.value) return
  currentIndex.value = (currentIndex.value + 1) % total.value
}
const prev = () => {
  if (!hasMultiple.value) return
  currentIndex.value = (currentIndex.value - 1 + total.value) % total.value
}

const handleKey = (e: KeyboardEvent) => {
  if (!props.modelValue) return
  if (e.key === 'Escape') close()
  else if (e.key === 'ArrowRight') next()
  else if (e.key === 'ArrowLeft') prev()
}

// Lock body scroll + bind keyboard handler while open.
watch(() => props.modelValue, (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKey)
  } else {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', handleKey)
  }
})

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', handleKey)
  }
})

const currentImage = computed(() => props.images[currentIndex.value] ?? props.images[0] ?? '')
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
        role="dialog"
        aria-modal="true"
        :aria-label="t('productPage.lightbox.label')"
      >
        <!-- Top bar: counter + close -->
        <div class="flex items-center justify-between px-4 py-3 text-white relative z-10">
          <span class="text-sm font-medium tabular-nums opacity-80">
            {{ hasMultiple ? `${currentIndex + 1} / ${total}` : '' }}
          </span>
          <button
            type="button"
            class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            :aria-label="t('productPage.lightbox.close')"
            @click="close"
          >
            <UIcon name="i-lucide-x" class="w-6 h-6" />
          </button>
        </div>

        <!-- Image area (click backdrop to close) -->
        <div
          class="flex-1 flex items-center justify-center relative overflow-hidden px-4"
          @click.self="close"
        >
          <button
            v-if="hasMultiple"
            type="button"
            class="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            :aria-label="t('productPage.lightbox.previous')"
            @click="prev"
          >
            <UIcon name="i-lucide-chevron-left" class="w-7 h-7" />
          </button>

          <img
            :src="imageUrl(currentImage, { w: 1600, h: 1600, fit: 'contain' })"
            :alt="alt"
            class="max-h-full max-w-full object-contain select-none"
            draggable="false"
            @click.stop
          >

          <button
            v-if="hasMultiple"
            type="button"
            class="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            :aria-label="t('productPage.lightbox.next')"
            @click="next"
          >
            <UIcon name="i-lucide-chevron-right" class="w-7 h-7" />
          </button>
        </div>

        <!-- Thumbnail strip -->
        <div
          v-if="hasMultiple"
          class="flex justify-center gap-2 px-4 py-4 overflow-x-auto"
        >
          <button
            v-for="(img, i) in images"
            :key="i"
            type="button"
            :class="[
              'flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-white/10 border-2 transition-all',
              i === currentIndex
                ? 'border-white'
                : 'border-transparent opacity-60 hover:opacity-100',
            ]"
            :aria-label="`${alt} ${i + 1}`"
            @click="currentIndex = i"
          >
            <img
              :src="imageUrl(img, { w: 120, h: 120, fit: 'contain' })"
              :alt="`${alt} ${i + 1}`"
              class="w-full h-full object-contain p-1"
            >
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
